import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { Button } from "~/components/ui";
import { cn } from "~/lib/utils";

interface CopyButtonProps {
  /** The text copied to the clipboard. */
  value: string;
  /** Accessible label (defaults to "copy"). */
  label?: string;
  /** Toast message key shown on success (defaults to the generic "copied"). */
  successMessageKey?: string;
  className?: string;
}

/** Small ghost icon button that copies `value` to the clipboard and toasts. */
export function CopyButton({ value, label, successMessageKey = "feature.common.copied", className }: CopyButtonProps) {
  const { t } = useTranslation();

  const onCopy = async () => {
    try {
      await copyToClipboard(value);
      toast.success(t(successMessageKey));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn("h-5 w-5 shrink-0 text-fg-3", className)}
      aria-label={label ?? t("ui.copy")}
      onClick={onCopy}
    >
      <Copy className="h-3 w-3" />
    </Button>
  );
}
